import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import wmsBaseService from '../../../service/wms/wmsBase.service';
import { useLoader } from '../../../provider/LoaderProvider';
import { dataGridPremiumColumns } from '../../../utils/helper';
import DeleteButton from '../Components/DeleteButton';
import NextButton from '../Components/NextButton';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { DataGridIdentifiers } from '../../Interface/Datagrid/DataGridIdentifiers';
import DatagridPremiumUi from '../../Interface/DatagridPremmium/DataGridPremiumUI';
import { WmsType } from '../../../config/wms/wmsType';

const wmsType = WmsType.PurchaseDelivery;

const InprogressPurchaseDelivery = ({ autocompleteData }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const handleFetchData = async () => {
    setLoading(true);
    showLoader();
    try {
      const data = await wmsBaseService.getData({ wmsType });
      setData(data.rows);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleContinue = (wmsEntry) => {
    navigate(`../purchaseDeliveryDoc/${wmsEntry}`);
  };

  const handleDelete = async (wmsEntry) => {
    showLoader();
    try {
      await wmsBaseService.deleteData(wmsEntry, wmsType);
      handleFetchData();
      return addNotification({
        content: `Sikeres törlés!`,
        type: NotificationType.WARNING,
      });
    } catch (e) {
      addNotification({
        content: `Hiba történt a törlés során!`,
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const warehouseMap = useMemo(
    () => new Map((autocompleteData?.warehouse ?? []).map((warehouse) => [warehouse.code, warehouse])),
    [autocompleteData]
  );

  const companyMap = useMemo(
    () => new Map((autocompleteData?.company ?? []).map((company) => [company.id, company])),
    [autocompleteData]
  );

  const userMap = useMemo(
    () => new Map((autocompleteData?.user ?? []).map((user) => [user.userId, user])),
    [autocompleteData]
  );

  const columns = dataGridPremiumColumns([
    {
      field: 'id',
      headerName: '#',
      flex: 0.25,
    },
    {
      field: 'createdAt',
      headerName: 'Folyamatban',
      flex: 1,
      renderCell: (data) => {
        const company = companyMap.get(data.row?.companyId);
        const warehouse = warehouseMap.get(data.row?.whsCode);
        const createdByUser = userMap.get(data.row?.createdBy);
        const supplierLabel = [company?.code ?? data.row?.companyCode, company?.name ?? data.row?.companyName]
          .filter(Boolean)
          .join(' - ');
        const warehouseLabel = [data.row?.whsCode, warehouse?.name].filter(Boolean).join(' - ');
        const createdByLabel =
          createdByUser?.nickName ??
          data.row?.userName ??
          data.row?.UserName ??
          data.row?.createdByName ??
          data.row?.createdBy ??
          '-';

        return (
          <div>
            <Typography variant="body1" fontWeight="bold">
              {warehouseLabel || '-'}
            </Typography>
            <Typography variant="body2">ÜP: {supplierLabel || '-'}</Typography>
            {data.row?.baseDocNum ? (
              <Typography variant="body2">Forrásbizonylat: {data.row.baseDocNum}</Typography>
            ) : null}
            <Typography variant="body2">Létrehozta: {createdByLabel}</Typography>
            <Typography variant="body2">{dayjs(data.row.createdAt).format('YYYY.MM.DD HH:mm')}</Typography>
          </div>
        );
      },
    },

    {
      field: 'operators',
      headerName: '',
      flex: 0.45,
      sortable: false,
      renderCell: (data) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <NextButton onClick={() => handleContinue(data.row.id)} />
            <DeleteButton onClick={() => handleDelete(data.row.id)} />
          </Box>
        );
      },
    },
  ]);

  return (
    <Box sx={{ pb: 2, pt: 3, px: 0, width: '100%' }}>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.wmsPurchaseDeliveryInProgress}
        rows={data}
        columns={columns}
        loading={loading}
        sx={{ width: '100%' }}
        getRowHeight={() => 'auto'}
        headerFilters={false}
        slots={{
          toolbar: null,
        }}
      />
    </Box>
  );
};

export default InprogressPurchaseDelivery;
